import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  OUTREACH_LANGUAGES, OUTREACH_VARIABLES,
  type CrmPerson, type InboundMail, type OutreachCampaign, type OutreachCampaignDetail, type OutreachRendered, type OutreachReviewItem, type OutreachSend,
  type OutreachStatus, type OutreachTemplate,
} from '@aviaconsole/shared';
import { capi, companyUrl } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n, type Key } from '../../i18n';
import { formatDate } from '../../lib/format';
import { useToast } from '../../components/Toast';
import { Button, Card, Chip, Empty, Input, Loading, Modal, PageHeader, Select, StatCard, Textarea } from '../../components/ui';
import { crmLabel, memberTone } from '../../lib/crm';

type Tab = 'campaigns' | 'replies' | 'templates' | 'sends' | 'review' | 'settings';

/** Outreach: the pause state and today's numbers, campaigns, replies, templates, the send log, manual review and settings. */
export function CrmOutreach() {
  const { role } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('campaigns');
  const status = useQuery({ queryKey: ['outreach-status'], queryFn: () => capi<OutreachStatus>('/crm/outreach'), refetchInterval: 30_000 });
  const resume = useMutation({
    mutationFn: () => capi<OutreachStatus>('/crm/outreach/resume', { method: 'POST' }),
    onSuccess: (s) => qc.setQueryData(['outreach-status'], s), onError: (e: Error) => toast.push(e.message, 'error'),
  });
  if (!status.data) return <Loading error={status.error} />;
  const s = status.data;
  const tabs: Tab[] = ['campaigns', 'replies', 'templates', 'sends', 'review', ...(role === 'admin' ? ['settings' as const] : [])];
  const label: Record<Tab, Key> = { campaigns: 'orCampaigns', replies: 'orReplies', templates: 'orTemplates', sends: 'orSendLog', review: 'orReview', settings: 'orSettings' };

  return (
    <>
      <PageHeader crumb={`${t('crmCrumb')} · ${s.sendingDomain}`} title={t('orTitle')} sub={t('orSub')} />
      <div className="stack">
        {!s.enabled && <div className="banner tint-warning"><i className="fas fa-power-off" aria-hidden="true" /><span>{t('orDisabled')}</span></div>}
        {s.paused && (
          <div className="banner tint-danger row">
            <i className="fas fa-circle-pause" aria-hidden="true" />
            <span style={{ flex: 1 }}>{t('orPaused', { scope: t(`orScope_${s.paused.scope}` as Key), reason: s.paused.reason })}</span>
            {role === 'admin' && <Button size="sm" onClick={() => resume.mutate()} disabled={resume.isPending}>{t('orResume')}</Button>}
          </div>
        )}
        <div className="grid kpis">
          <StatCard label={t('orSendsToday')} value={String(s.today.sends)} caption={t('orDayCap', { n: s.today.dayCap })} icon="fas fa-paper-plane" />
          <StatCard label={t('orBouncesToday')} value={String(s.today.hardBounces)} icon="fas fa-reply" tone={s.today.hardBounces ? 'danger' : undefined} />
          <StatCard label={t('orComplaintsToday')} value={String(s.today.complaints)} icon="fas fa-flag" tone={s.today.complaints ? 'danger' : undefined} />
        </div>
        <div className="pillgroup" role="tablist">
          {tabs.map((x) => <button key={x} type="button" role="tab" aria-selected={tab === x} className={`pill ${tab === x ? 'active' : ''}`} onClick={() => setTab(x)}>{t(label[x])}</button>)}
        </div>
        {tab === 'campaigns' && <Campaigns />}
        {tab === 'replies' && <Replies />}
        {tab === 'templates' && <Templates domain={s.sendingDomain} />}
        {tab === 'sends' && <Sends />}
        {tab === 'review' && <Review />}
        {tab === 'settings' && <Settings status={s} />}
      </div>
    </>
  );
}

// ---- campaigns ----------------------------------------------------------------------------

function Campaigns() {
  const { t, lang } = useI18n();
  const list = useQuery({ queryKey: ['outreach-campaigns'], queryFn: () => capi<OutreachCampaign[]>('/crm/outreach/campaigns'), refetchInterval: 30_000 });
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card pad={false} className="table">
      <div className="cardhead"><h2>{t('orCampaigns')}</h2><Button size="sm" icon="fas fa-plus" onClick={() => setCreating(true)}>{t('orNewCampaign')}</Button></div>
      <table><tbody>
        {list.data?.length === 0 && <Empty />}
        {list.data?.map((c) => (
          <tr key={c.id} onClick={() => setOpen(c.id)} style={{ cursor: 'pointer' }}>
            <td><div style={{ fontWeight: 600 }}>{c.name}</div><div className="sub">{c.templateName} · {formatDate(c.createdAt, lang)}</div></td>
            <td>{Object.entries(c.counts).map(([k, n]) => <Chip key={k} tone={memberTone(k)}>{crmLabel('memberStatus', k, lang)} {n}</Chip>)}</td>
            <td className="r"><Chip>{crmLabel('campaignStatus', c.status, lang)}</Chip></td>
          </tr>
        ))}
      </tbody></table>
      {creating && <CampaignForm onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setOpen(id); }} />}
      {open && <CampaignDetail id={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}

/** Pick people by searching; the selection survives new searches. */
function PeoplePicker({ selected, onChange }: { selected: Map<string, string>; onChange: (m: Map<string, string>) => void }) {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const people = useQuery({ queryKey: ['crm-people-pick', q], queryFn: () => capi<CrmPerson[]>(`/crm/people?limit=50&optedOut=false${q ? `&q=${encodeURIComponent(q)}` : ''}`) });
  const toggle = (p: CrmPerson) => { const m = new Map(selected); if (m.has(p.id)) m.delete(p.id); else m.set(p.id, p.name); onChange(m); };
  return (
    <div className="stack sm">
      <Input label={t('orSearchPeople')} value={q} onChange={(e) => setQ(e.target.value)} />
      <div style={{ maxHeight: '14rem', overflow: 'auto' }}>
        <table className="small"><tbody>
          {people.data?.map((p) => (
            <tr key={p.id} onClick={() => toggle(p)} style={{ cursor: 'pointer' }}>
              <td style={{ width: '2rem' }}><input type="checkbox" readOnly checked={selected.has(p.id)} aria-label={p.name} /></td>
              <td>{p.name}</td><td className="sub">{p.accountName}</td><td className="sub">{p.email ?? ''}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      <div className="small muted">{t('orSelected', { n: selected.size })}</div>
    </div>
  );
}

function CampaignForm({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ['outreach-templates'], queryFn: () => capi<OutreachTemplate[]>('/crm/outreach/templates') });
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [selected, setSelected] = useState(new Map<string, string>());
  const save = useMutation({
    mutationFn: () => capi<OutreachCampaignDetail>('/crm/outreach/campaigns', { method: 'POST', json: { name, templateId: templateId || templates.data?.[0]?.id, personIds: [...selected.keys()] } }),
    onSuccess: (c) => { qc.invalidateQueries({ queryKey: ['outreach-campaigns'] }); onCreated(c.id); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <Modal title={t('orNewCampaign')} onClose={onClose}>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid fields">
          <Input label={t('orName')} required value={name} onChange={(e) => setName(e.target.value)} />
          <Select label={t('orTemplate')} options={(templates.data ?? []).map((x) => ({ value: x.id, label: `${x.name} (v${x.version})` }))} value={templateId} onChange={(e) => setTemplateId(e.target.value)} />
        </div>
        <PeoplePicker selected={selected} onChange={setSelected} />
        <div className="row" style={{ justifyContent: 'flex-end' }}><Button onClick={onClose}>{t('cancel')}</Button><Button variant="primary" type="submit" disabled={save.isPending || !templates.data?.length}>{t('crmCreate')}</Button></div>
      </form>
    </Modal>
  );
}

function CampaignDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, lang } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const c = useQuery({ queryKey: ['outreach-campaign', id], queryFn: () => capi<OutreachCampaignDetail>(`/crm/outreach/campaigns/${id}`), refetchInterval: 15_000 });
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(new Map<string, string>());
  const done = (d: OutreachCampaignDetail) => { qc.setQueryData(['outreach-campaign', id], d); qc.invalidateQueries({ queryKey: ['outreach-campaigns'] }); };
  const act = useMutation({ mutationFn: (a: string) => capi<OutreachCampaignDetail>(`/crm/outreach/campaigns/${id}/${a}`, { method: 'POST' }), onSuccess: done, onError: (e: Error) => toast.push(e.message, 'error') });
  const add = useMutation({
    mutationFn: () => capi<OutreachCampaignDetail>(`/crm/outreach/campaigns/${id}/members`, { method: 'POST', json: { personIds: [...selected.keys()] } }),
    onSuccess: (d) => { done(d); setAdding(false); setSelected(new Map()); }, onError: (e: Error) => toast.push(e.message, 'error'),
  });
  if (!c.data) return <Modal title="…" onClose={onClose}><Loading error={c.error} /></Modal>;
  const d = c.data;
  return (
    <Modal title={d.name} onClose={onClose}>
      <div className="stack md">
        <div className="row">
          <Chip>{crmLabel('campaignStatus', d.status, lang)}</Chip><span className="small muted">{d.templateName}</span>
          <span style={{ flex: 1 }} />
          {d.status === 'active' && <Button size="sm" onClick={() => act.mutate('pause')}>{t('orPause')}</Button>}
          {d.status === 'paused' && <Button size="sm" onClick={() => act.mutate('resume')}>{t('orResume')}</Button>}
          {(d.status === 'active' || d.status === 'paused') && <Button size="sm" variant="danger" onClick={() => act.mutate('cancel')}>{t('orCancelCampaign')}</Button>}
        </div>
        <div style={{ maxHeight: '22rem', overflow: 'auto' }}>
          <table className="small"><tbody>
            {d.members.map((m) => (
              <tr key={m.id}>
                <td><div style={{ fontWeight: 600 }}>{m.personName}</div><div className="sub">{m.accountName}</div></td>
                <td>{m.currentAddress ?? ''}</td>
                <td><Chip tone={memberTone(m.status)}>{crmLabel('memberStatus', m.status, lang)}</Chip>{m.detail && <div className="sub">{m.detail}</div>}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        {adding ? (
          <div className="stack sm">
            <PeoplePicker selected={selected} onChange={setSelected} />
            <div className="row" style={{ justifyContent: 'flex-end' }}><Button onClick={() => setAdding(false)}>{t('cancel')}</Button><Button variant="primary" disabled={!selected.size || add.isPending} onClick={() => add.mutate()}>{t('orAddPeople')}</Button></div>
          </div>
        ) : d.status !== 'cancelled' && <div><Button size="sm" icon="fas fa-user-plus" onClick={() => setAdding(true)}>{t('orAddPeople')}</Button></div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}><Button onClick={onClose}>{t('segClose')}</Button></div>
      </div>
    </Modal>
  );
}

// ---- templates ----------------------------------------------------------------------------

function Templates({ domain }: { domain: string }) {
  const { t, lang } = useI18n();
  const list = useQuery({ queryKey: ['outreach-templates'], queryFn: () => capi<OutreachTemplate[]>('/crm/outreach/templates') });
  const [editing, setEditing] = useState<OutreachTemplate | 'new' | null>(null);
  return (
    <Card pad={false} className="table">
      <div className="cardhead"><h2>{t('orTemplates')}</h2><Button size="sm" icon="fas fa-plus" onClick={() => setEditing('new')}>{t('orNewTemplate')}</Button></div>
      <table><tbody>
        {list.data?.length === 0 && <Empty />}
        {list.data?.map((x) => (
          <tr key={x.id} onClick={() => setEditing(x)} style={{ cursor: 'pointer' }}>
            <td><div style={{ fontWeight: 600 }}>{x.name}</div><div className="sub">{x.subject}</div></td>
            <td className="small">{x.language.toUpperCase()} · v{x.version}</td>
            <td className="small muted r">{x.senderName} &lt;{x.senderEmail}&gt; · {formatDate(x.updatedAt, lang)}</td>
          </tr>
        ))}
      </tbody></table>
      {editing && <TemplateForm template={editing === 'new' ? null : editing} domain={domain} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function TemplateForm({ template, domain, onClose }: { template: OutreachTemplate | null; domain: string; onClose: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: template?.name ?? '', language: template?.language ?? 'nl', subject: template?.subject ?? '',
    bodyHtml: template?.bodyHtml ?? '<p>Beste {{contact.first_name}},</p>\n<p></p>\n<p>Met vriendelijke groet,<br>{{sender.name}}</p>\n<p style="font-size:12px;color:#777"><a href="{{unsubscribe_url}}">Afmelden</a></p>',
    bodyText: template?.bodyText ?? '', senderName: template?.senderName ?? '', senderEmail: template?.senderEmail ?? `@${domain}`,
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const [previewFor, setPreviewFor] = useState('');
  const [preview, setPreview] = useState<OutreachRendered | null>(null);
  const people = useQuery({ queryKey: ['crm-people-preview'], queryFn: () => capi<CrmPerson[]>('/crm/people?limit=100') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['outreach-templates'] });
  const fail = (e: Error) => toast.push(e.message, 'error');
  const save = useMutation({
    mutationFn: () => template
      ? capi<OutreachTemplate>(`/crm/outreach/templates/${template.id}`, { method: 'PATCH', json: { ...f, bodyText: f.bodyText || null } })
      : capi<OutreachTemplate>('/crm/outreach/templates', { method: 'POST', json: { ...f, bodyText: f.bodyText || null } }),
    onSuccess: () => { refresh(); toast.push(t('done')); onClose(); }, onError: fail,
  });
  const archive = useMutation({ mutationFn: () => capi(`/crm/outreach/templates/${template!.id}`, { method: 'DELETE' }), onSuccess: () => { refresh(); onClose(); }, onError: fail });
  const doPreview = useMutation({
    mutationFn: () => capi<OutreachRendered>(`/crm/outreach/templates/${template!.id}/preview`, { method: 'POST', json: { personId: previewFor } }),
    onSuccess: setPreview, onError: fail,
  });
  const test = useMutation({
    mutationFn: () => capi<OutreachSend>(`/crm/outreach/templates/${template!.id}/test`, { method: 'POST', json: previewFor ? { personId: previewFor } : {} }),
    onSuccess: (s) => toast.push(t('orTestSent', { to: s.address })), onError: fail,
  });
  return (
    <Modal title={template ? `${template.name} · v${template.version}` : t('orNewTemplate')} onClose={onClose}>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid fields">
          <Input label={t('orName')} required value={f.name} onChange={set('name')} />
          <Select label={t('orLanguage')} options={OUTREACH_LANGUAGES.map((l) => ({ value: l, label: l.toUpperCase() }))} value={f.language} onChange={set('language')} />
          <Input label={t('orSenderName')} required value={f.senderName} onChange={set('senderName')} />
          <Input label={t('orSenderEmail', { domain })} type="email" required value={f.senderEmail} onChange={set('senderEmail')} />
        </div>
        <Input label={t('orSubject')} required value={f.subject} onChange={set('subject')} />
        <Textarea label={t('orBodyHtml')} required rows={9} value={f.bodyHtml} onChange={set('bodyHtml')} style={{ fontFamily: 'var(--font-mono, monospace)' }} />
        <Textarea label={t('orBodyText')} rows={4} value={f.bodyText} onChange={set('bodyText')} />
        <div className="small muted">{t('orVariables', { list: OUTREACH_VARIABLES.map((v) => `{{${v}}}`).join(' ') })}</div>
        {template && (
          <div className="row">
            <Select label={t('orPreviewFor')} options={[{ value: '', label: '—' }, ...(people.data ?? []).map((p) => ({ value: p.id, label: `${p.name} · ${p.accountName}` }))]} value={previewFor} onChange={(e) => setPreviewFor(e.target.value)} />
            <Button disabled={!previewFor || doPreview.isPending} onClick={() => doPreview.mutate()}>{t('orPreview')}</Button>
            <Button icon="fas fa-paper-plane" disabled={test.isPending} onClick={() => test.mutate()}>{t('orTestSend')}</Button>
          </div>
        )}
        {preview && (
          <Card tint={preview.missing.length ? 'warning' : undefined}>
            <div style={{ fontWeight: 600 }}>{preview.subject}</div>
            {preview.missing.length > 0 && <div className="small" style={{ color: 'var(--danger)' }}>{preview.missing.map((m) => `{{${m}}}`).join(', ')}</div>}
            {/* Rendered in a sandboxed frame: the HTML is the user's own template, but it never runs scripts here. */}
            <iframe title={t('orPreview')} sandbox="" srcDoc={preview.html} style={{ width: '100%', minHeight: '32rem', border: 0, background: '#04070D', borderRadius: 8, marginTop: '0.5rem' }} />
          </Card>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          {template && <Button variant="danger" onClick={() => archive.mutate()} disabled={archive.isPending}>{t('orArchive')}</Button>}
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>{template ? t('crmSave') : t('crmCreate')}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ---- replies -------------------------------------------------------------------------------

/** Every answer to outreach in full, newest first; the same text is on the account's timeline. */
function Replies() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['outreach-replies'], queryFn: () => capi<InboundMail[]>('/crm/outreach/replies?limit=200'), refetchInterval: 60_000 });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card pad={false} className="table">
      <div className="cardhead"><h2>{t('orReplies')}</h2></div>
      <table className="small"><tbody>
        {list.data?.length === 0 && <Empty>{t('orRepliesEmpty')}</Empty>}
        {list.data?.map((r) => (
          <tr key={r.id} onClick={() => setOpen(open === r.id ? null : r.id)} style={{ cursor: 'pointer' }}>
            <td className="muted" style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{formatDate(r.receivedAt, lang)}</td>
            <td style={{ verticalAlign: 'top' }}>
              <div style={{ fontWeight: 600 }}>{r.personName ?? r.fromName ?? r.fromAddress}{r.kind === 'auto_reply' && <> <Chip>{t('orReplyAuto')}</Chip></>}</div>
              <div className="sub">{[r.accountName, r.fromAddress].filter(Boolean).join(' · ')}</div>
              <div>{r.subject || '—'}</div>
              {open === r.id
                ? <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '24rem', overflow: 'auto', fontFamily: 'inherit', margin: '0.5rem 0 0' }}>{r.bodyText ?? ''}</pre>
                : <div className="sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '40rem' }}>{r.bodyText ?? ''}</div>}
            </td>
            <td className="r" style={{ verticalAlign: 'top' }}>
              {r.forwardedTo && <div className="sub">{t('orReplyForwarded', { to: r.forwardedTo })}</div>}
              <span className="row" style={{ justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                {r.accountId && <button type="button" className="linkbtn" onClick={() => navigate(`/crm/accounts/${r.accountId}`)}>{t('orReplyOpenAccount')}</button>}
                <button type="button" className="linkbtn" onClick={() => window.open(companyUrl(`/crm/outreach/replies/${r.id}/raw`), '_blank')}>{t('inMailOriginal')}</button>
              </span>
            </td>
          </tr>
        ))}
      </tbody></table>
    </Card>
  );
}

// ---- send log, review, settings -------------------------------------------------------------

function Sends() {
  const { t, lang } = useI18n();
  const list = useQuery({ queryKey: ['outreach-sends'], queryFn: () => capi<OutreachSend[]>('/crm/outreach/sends?limit=200'), refetchInterval: 30_000 });
  return (
    <Card pad={false} className="table">
      <div className="cardhead"><h2>{t('orSendLog')}</h2></div>
      <table className="small"><tbody>
        {list.data?.length === 0 && <Empty />}
        {list.data?.map((s) => (
          <tr key={s.id}>
            <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(s.createdAt, lang)}</td>
            <td><div>{s.personName ?? (s.kind === 'test' ? 'test' : '—')}</div><div className="sub">{s.address}</div></td>
            <td>{s.subject}{s.templateVersion ? <span className="muted"> · v{s.templateVersion}</span> : null}</td>
            <td><Chip tone={s.status === 'bounced' || s.status === 'failed' ? 'danger' : s.status === 'delivered' || s.status === 'accepted' ? 'success' : 'neutral'}>{s.status}</Chip>
              {(s.smtpCode || s.detail) && <div className="sub">{[s.smtpCode, s.smtpText ?? s.detail].filter(Boolean).join(' ')}</div>}</td>
          </tr>
        ))}
      </tbody></table>
    </Card>
  );
}

function Review() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['outreach-review'], queryFn: () => capi<OutreachReviewItem[]>('/crm/outreach/review') });
  const close = useMutation({ mutationFn: (id: string) => capi(`/crm/outreach/review/${id}/done`, { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach-review'] }) });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card pad={false} className="table">
      <div className="cardhead"><h2>{t('orReview')}</h2></div>
      <table className="small"><tbody>
        {list.data?.length === 0 && <Empty>{t('orReviewEmpty')}</Empty>}
        {list.data?.map((r) => (
          <tr key={r.id}>
            <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(r.receivedAt, lang)}</td>
            <td><div>{r.signal}</div><div className="sub">{r.detail}</div>
              {open === r.id && r.raw && <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '16rem', overflow: 'auto', fontSize: '0.72rem' }}>{r.raw}</pre>}</td>
            <td className="r"><span className="row" style={{ justifyContent: 'flex-end' }}>
              {r.raw && <button type="button" className="linkbtn" onClick={() => setOpen(open === r.id ? null : r.id)}>{t('orPreview')}</button>}
              <button type="button" className="linkbtn" onClick={() => close.mutate(r.id)}>{t('orReviewDone')}</button>
            </span></td>
          </tr>
        ))}
      </tbody></table>
    </Card>
  );
}

function Settings({ status }: { status: OutreachStatus }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const s = status.settings;
  const [f, setF] = useState({
    maxPerHour: String(s.maxPerHour), maxPerDomainPerHour: String(s.maxPerDomainPerHour), maxPerDay: String(s.maxPerDay),
    warmupStart: s.warmup.startDate ?? '', from: s.window.from, to: s.window.to, waitHours: String(s.waitHours),
    maxCandidates: String(s.maxCandidates), bounce: String(s.maxHardBounceRate * 100), retention: String(s.retentionMonths),
    forwardTo: s.replyForwardTo ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => capi<OutreachStatus>('/crm/outreach/settings', {
      method: 'PATCH', json: {
        maxPerHour: Number(f.maxPerHour), maxPerDomainPerHour: Number(f.maxPerDomainPerHour), maxPerDay: Number(f.maxPerDay),
        warmup: { ...s.warmup, startDate: f.warmupStart || null }, window: { ...s.window, from: f.from, to: f.to },
        waitHours: Number(f.waitHours), maxCandidates: Number(f.maxCandidates), maxHardBounceRate: Number(f.bounce) / 100, retentionMonths: Number(f.retention),
        replyForwardTo: f.forwardTo.trim() || null,
      },
    }),
    onSuccess: (x) => { qc.setQueryData(['outreach-status'], x); toast.push(t('done')); }, onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <Card>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid fields">
          <Input label={t('orMaxPerHour')} type="number" min={1} value={f.maxPerHour} onChange={set('maxPerHour')} />
          <Input label={t('orMaxPerDomain')} type="number" min={1} value={f.maxPerDomainPerHour} onChange={set('maxPerDomainPerHour')} />
          <Input label={t('orMaxPerDay')} type="number" min={1} value={f.maxPerDay} onChange={set('maxPerDay')} />
          <Input label={t('orWarmup')} type="date" value={f.warmupStart} onChange={set('warmupStart')} />
          <Input label={t('orWindowFrom')} type="time" value={f.from} onChange={set('from')} />
          <Input label={t('orWindowTo')} type="time" value={f.to} onChange={set('to')} />
          <Input label={t('orWaitHours')} type="number" min={1} value={f.waitHours} onChange={set('waitHours')} />
          <Input label={t('orMaxCandidates')} type="number" min={1} max={20} value={f.maxCandidates} onChange={set('maxCandidates')} />
          <Input label={t('orBounceRate')} type="number" min={0} step="0.1" value={f.bounce} onChange={set('bounce')} />
          <Input label={t('orRetention')} type="number" min={1} value={f.retention} onChange={set('retention')} />
          <Input label={t('orReplyForwardTo')} type="email" value={f.forwardTo} onChange={set('forwardTo')} placeholder="sales@…" />
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}><Button variant="primary" type="submit" disabled={save.isPending}>{t('crmSave')}</Button></div>
      </form>
    </Card>
  );
}
